import React from 'react';

import { Icon, IconBus, IconCopy, IconTrain } from '@flixbus/honeycomb-icons-react';
import { Link } from '@flixbus/honeycomb-react';

import BreakdownCard from 'common/BreakdownCard';
import { handleCopyRideId } from 'pages/RideDetails/utils/copyToClipboard';
import VehicleAssignmentTag from 'pages/RideListPage/components/VehicleAssignmentTag';
import { VEHICLE_TYPE_BUS } from 'pages/RideListPage/const';
import { calculateDuration } from 'utils/datetime';
import { getOneOpsRideUrl, isProduction } from 'utils/getRunPlanningUrl';

import { buildDepartureLabel } from './buildDepartureLabel';
import { deriveUniqueVehicleAssignments } from './deriveUniqueVehicleAssignments';
import { DETAILS_HEADER } from './text';

import styles from './styles.module.css';

/**
 * @typedef {object} DetailsHeaderDetails
 * @property {string} lineCode
 * @property {number | string} tripNumber
 * @property {string} id
 * @property {string} [stationFirst]
 * @property {string} [stationLast]
 * @property {string | null} [departure]
 * @property {string | undefined} [firstStationTimezone]
 */

/**
 * @typedef {object} DetailsHeaderProps
 * @property {DetailsHeaderDetails} details
 * @property {Array<{busNumber?: string | null, vehicleType?: string}>} [vehicleAssignments]
 * @property {Array<{code?: string, name?: string, arrival?: string | null}>} [stations]
 * @property {object | null} [breakdown]
 * @property {any[]} [workAssignmentSequences]
 */

/** @param {DetailsHeaderProps} props */
const DetailsHeader = ({
	details,
	vehicleAssignments = [],
	stations = [],
	breakdown,
	workAssignmentSequences = [],
}) => {
	const { lineCode, tripNumber, id, stationFirst, stationLast, departure, firstStationTimezone } =
		details;

	const firstStation = stations[0];
	const lastStation = stations[stations.length - 1];
	const firstCode = firstStation?.code ?? '';
	const lastCode = lastStation?.code ?? '';
	const firstName = firstStation?.name ?? stationFirst;
	const lastName = lastStation?.name ?? stationLast;

	const lastStationArrival = lastStation?.arrival;
	const estimatedDuration =
		departure && lastStationArrival ? calculateDuration(departure, lastStationArrival, true) : null;

	const departureLabel = buildDepartureLabel(departure, firstStationTimezone);

	const uniqueVehicleAssignments = deriveUniqueVehicleAssignments(vehicleAssignments);
	const vehicleTags =
		uniqueVehicleAssignments.length > 0
			? uniqueVehicleAssignments.map((va) => {
					const iconType = va.vehicleType === VEHICLE_TYPE_BUS ? IconBus : IconTrain;
					return (
						<VehicleAssignmentTag
							key={va.busNumber}
							vehicleAssignment={va}
							icon={iconType}
							fallbackLabel={DETAILS_HEADER.na}
						/>
					);
				})
			: DETAILS_HEADER.na;

	return (
		<div className={styles.header}>
			<div className={styles.container}>
				<div className={styles.mainBox}>
					<div className={styles.leftColumn}>
						<h1
							className={styles.title}
						>{`${DETAILS_HEADER.line} ${lineCode} / ${DETAILS_HEADER.tripNumber} ${tripNumber}`}</h1>
						<div className={styles.stationCodes}>
							<div className={styles.stationBlock}>
								<span className={styles.code}>{firstCode}</span>
								<span className={styles.stationName}>{firstName}</span>
							</div>
							<span className={styles.arrow}>→</span>
							<div className={styles.stationBlock}>
								<span className={styles.code}>{lastCode}</span>
								<span className={styles.stationName}>{lastName}</span>
							</div>
						</div>
					</div>
					<div className={styles.middleColumn}>
						<div className={styles.infoRow}>
							<span className={styles.label}>{DETAILS_HEADER.departure}</span>
							<span>{departureLabel}</span>
						</div>
						{estimatedDuration && (
							<div className={styles.infoRow}>
								<span className={styles.label}>{DETAILS_HEADER.estimatedDuration}</span>
								<span>{estimatedDuration}</span>
							</div>
						)}
						<div className={styles.infoRow}>
							<span className={styles.label}>{DETAILS_HEADER.vehicles}</span>
							<span className={styles.vehicleTags}>{vehicleTags}</span>
						</div>
						<div className={styles.uuidRow}>
							<span className={styles.label}>{DETAILS_HEADER.rideUuid}</span>
							{isProduction() ? (
								<Link href={getOneOpsRideUrl(id)} target="_blank">
									{id}
								</Link>
							) : (
								<span>{id}</span>
							)}
							<Icon
								extraClasses={styles.copyIcon}
								InlineIcon={IconCopy}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleCopyRideId(id);
								}}
							/>
						</div>
					</div>
				</div>

				{breakdown && (
					<BreakdownCard
						breakdown={breakdown}
						timezone={firstStationTimezone}
						className={styles.rightColumn}
						sequences={workAssignmentSequences}
					/>
				)}
			</div>
		</div>
	);
};

export default DetailsHeader;
